import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { UserContext } from '../App';
import {
  CALENDAR_CARD_APPEARANCE_STORAGE_KEY,
  DEFAULT_CALENDAR_CARD_APPEARANCE,
  sanitizeCalendarCardAppearance,
} from '../utils/calendarCardAppearance';
import {
  DEFAULT_THEME_ID,
  THEME_ACCENT_OVERRIDES_STORAGE_KEY,
  THEME_BACKGROUND_OVERRIDES_STORAGE_KEY,
  THEME_FAMILY_STORAGE_KEY,
  THEME_ID_STORAGE_KEY,
  THEME_TEXT_OVERRIDES_STORAGE_KEY,
  THEMES,
  applyThemeToDocument,
  getBaseThemeIdForFamily,
  getResolvedTheme,
  getThemeById,
  getThemeStorageValue,
  resolveThemeId,
  sanitizeHexColor,
  sanitizeThemeAccentOverrides,
  sanitizeThemeBackgroundOverrides,
  sanitizeThemeTextOverrides,
} from '../utils/themes';

const ThemeContext = createContext();

function writeThemePayloadToLocalStorage(payload) {
  try {
    localStorage.setItem(THEME_ID_STORAGE_KEY, payload.themeId);
    localStorage.setItem(THEME_FAMILY_STORAGE_KEY, payload.theme);
    localStorage.setItem(
      THEME_ACCENT_OVERRIDES_STORAGE_KEY,
      JSON.stringify(payload.themeAccentOverrides)
    );
    localStorage.setItem(
      THEME_BACKGROUND_OVERRIDES_STORAGE_KEY,
      JSON.stringify(payload.themeBackgroundOverrides)
    );
    localStorage.setItem(
      THEME_TEXT_OVERRIDES_STORAGE_KEY,
      JSON.stringify(payload.themeTextOverrides)
    );
  } catch (error) {
    // Ignore localStorage issues and keep the in-memory theme applied.
  }
}

function writeCalendarCardAppearanceToLocalStorage(appearance) {
  try {
    localStorage.setItem(
      CALENDAR_CARD_APPEARANCE_STORAGE_KEY,
      JSON.stringify(sanitizeCalendarCardAppearance(appearance))
    );
  } catch (error) {
    // Ignore localStorage issues and keep the in-memory colors applied.
  }
}

function getStoredCalendarCardAppearance() {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_CALENDAR_CARD_APPEARANCE };
  }

  try {
    const rawValue = localStorage.getItem(CALENDAR_CARD_APPEARANCE_STORAGE_KEY);
    return sanitizeCalendarCardAppearance(rawValue ? JSON.parse(rawValue) : undefined);
  } catch (error) {
    return { ...DEFAULT_CALENDAR_CARD_APPEARANCE };
  }
}

function getStoredLocalThemeId() {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME_ID;
  }

  try {
    return resolveThemeId(
      localStorage.getItem(THEME_ID_STORAGE_KEY),
      localStorage.getItem(THEME_FAMILY_STORAGE_KEY)
    );
  } catch (error) {
    return DEFAULT_THEME_ID;
  }
}

function getStoredLocalThemeAccentOverrides() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = localStorage.getItem(THEME_ACCENT_OVERRIDES_STORAGE_KEY);
    return sanitizeThemeAccentOverrides(rawValue ? JSON.parse(rawValue) : {});
  } catch (error) {
    return {};
  }
}

function getStoredLocalThemeBackgroundOverrides() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = localStorage.getItem(THEME_BACKGROUND_OVERRIDES_STORAGE_KEY);
    return sanitizeThemeBackgroundOverrides(rawValue ? JSON.parse(rawValue) : {});
  } catch (error) {
    return {};
  }
}

function getStoredLocalThemeTextOverrides() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = localStorage.getItem(THEME_TEXT_OVERRIDES_STORAGE_KEY);
    return sanitizeThemeTextOverrides(rawValue ? JSON.parse(rawValue) : {});
  } catch (error) {
    return {};
  }
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [themeId, setThemeId] = useState(DEFAULT_THEME_ID);
  const [themeAccentOverrides, setThemeAccentOverrides] = useState({});
  const [themeBackgroundOverrides, setThemeBackgroundOverrides] = useState({});
  const [themeTextOverrides, setThemeTextOverrides] = useState({});
  const [calendarCardAppearance, setCalendarCardAppearanceState] = useState(
    DEFAULT_CALENDAR_CARD_APPEARANCE
  );
  const [calendarCardStatus, setCalendarCardStatus] = useState('loading');
  const calendarCardAppearanceRef = useRef(DEFAULT_CALENDAR_CARD_APPEARANCE);
  const [loading, setLoading] = useState(true);
  let currentUser = null;

  try {
    const userContext = useContext(UserContext);
    currentUser = userContext?.currentUser || null;
  } catch (error) {
    currentUser = null;
  }

  const syncThemeState = useCallback((
    nextThemeId,
    nextAccentOverrides = {},
    nextBackgroundOverrides = {},
    nextTextOverrides = {},
    nextCalendarCardAppearance = calendarCardAppearanceRef.current
  ) => {
    const resolvedTheme = getThemeById(nextThemeId);
    const sanitizedAccentOverrides = sanitizeThemeAccentOverrides(nextAccentOverrides);
    const sanitizedBackgroundOverrides = sanitizeThemeBackgroundOverrides(nextBackgroundOverrides);
    const sanitizedTextOverrides = sanitizeThemeTextOverrides(nextTextOverrides);
    const sanitizedCalendarCardAppearance = sanitizeCalendarCardAppearance(
      nextCalendarCardAppearance
    );

    setThemeId(resolvedTheme.id);
    setThemeAccentOverrides(sanitizedAccentOverrides);
    setThemeBackgroundOverrides(sanitizedBackgroundOverrides);
    setThemeTextOverrides(sanitizedTextOverrides);
    calendarCardAppearanceRef.current = sanitizedCalendarCardAppearance;
    setCalendarCardAppearanceState(sanitizedCalendarCardAppearance);
    applyThemeToDocument(
      resolvedTheme.id,
      sanitizedAccentOverrides,
      sanitizedBackgroundOverrides,
      sanitizedTextOverrides,
      sanitizedCalendarCardAppearance
    );
  }, []);

  const persistTheme = useCallback(async (
    nextThemeId,
    nextAccentOverrides,
    nextBackgroundOverrides,
    nextTextOverrides,
    user
  ) => {
    const payload = getThemeStorageValue(
      nextThemeId,
      nextAccentOverrides,
      nextBackgroundOverrides,
      nextTextOverrides
    );

    writeThemePayloadToLocalStorage(payload);

    if (!user) {
      return;
    }

    const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
    await setDoc(prefsRef, payload, { merge: true });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadTheme = async () => {
      const localThemeId = getStoredLocalThemeId();
      const localAccentOverrides = getStoredLocalThemeAccentOverrides();
      const localBackgroundOverrides = getStoredLocalThemeBackgroundOverrides();
      const localTextOverrides = getStoredLocalThemeTextOverrides();
      const localCalendarCardAppearance = getStoredCalendarCardAppearance();

      if (!currentUser) {
        if (!cancelled) {
          syncThemeState(
            localThemeId,
            localAccentOverrides,
            localBackgroundOverrides,
            localTextOverrides,
            localCalendarCardAppearance
          );
          setCalendarCardStatus('preview');
          setLoading(false);
        }
        return;
      }

      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        const data = prefsSnap.exists() ? prefsSnap.data() : {};
        const remoteAccentOverrides = sanitizeThemeAccentOverrides(data.themeAccentOverrides);
        const remoteBackgroundOverrides = sanitizeThemeBackgroundOverrides(data.themeBackgroundOverrides);
        const remoteTextOverrides = sanitizeThemeTextOverrides(data.themeTextOverrides);
        const resolvedCalendarCardAppearance = data.calendarCardAppearance
          ? sanitizeCalendarCardAppearance(data.calendarCardAppearance)
          : localCalendarCardAppearance;
        const mergedAccentOverrides = sanitizeThemeAccentOverrides({
          ...remoteAccentOverrides,
          ...localAccentOverrides,
        });
        const mergedBackgroundOverrides = sanitizeThemeBackgroundOverrides({
          ...remoteBackgroundOverrides,
          ...localBackgroundOverrides,
        });
        const mergedTextOverrides = sanitizeThemeTextOverrides({
          ...remoteTextOverrides,
          ...localTextOverrides,
        });
        const hasRemoteThemePreference = Boolean(data.themeId || data.theme);
        const resolvedThemeId = hasRemoteThemePreference
          ? resolveThemeId(data.themeId, data.theme)
          : localThemeId;
        const payload = getThemeStorageValue(
          resolvedThemeId,
          mergedAccentOverrides,
          mergedBackgroundOverrides,
          mergedTextOverrides
        );

        if (!cancelled) {
          syncThemeState(
            resolvedThemeId,
            mergedAccentOverrides,
            mergedBackgroundOverrides,
            mergedTextOverrides,
            resolvedCalendarCardAppearance
          );
          setCalendarCardStatus('idle');
        }

        const needsWriteback =
          data.themeId !== payload.themeId ||
          data.theme !== payload.theme ||
          JSON.stringify(remoteAccentOverrides) !== JSON.stringify(payload.themeAccentOverrides) ||
          JSON.stringify(remoteBackgroundOverrides) !== JSON.stringify(payload.themeBackgroundOverrides) ||
          JSON.stringify(remoteTextOverrides) !== JSON.stringify(payload.themeTextOverrides);

        if (needsWriteback) {
          await setDoc(prefsRef, payload, { merge: true });
        }

        writeThemePayloadToLocalStorage(payload);
        writeCalendarCardAppearanceToLocalStorage(resolvedCalendarCardAppearance);
      } catch (error) {
        console.error('Error loading theme:', error);
        if (!cancelled) {
          syncThemeState(
            localThemeId,
            localAccentOverrides,
            localBackgroundOverrides,
            localTextOverrides,
            localCalendarCardAppearance
          );
          setCalendarCardStatus('error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTheme();

    return () => {
      cancelled = true;
    };
  }, [currentUser, persistTheme, syncThemeState]);

  const setTheme = useCallback(async (nextThemeId) => {
    const resolvedTheme = getThemeById(nextThemeId);
    syncThemeState(
      resolvedTheme.id,
      themeAccentOverrides,
      themeBackgroundOverrides,
      themeTextOverrides
    );

    try {
      await persistTheme(
        resolvedTheme.id,
        themeAccentOverrides,
        themeBackgroundOverrides,
        themeTextOverrides,
        currentUser
      );
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  }, [
    currentUser,
    persistTheme,
    syncThemeState,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeTextOverrides,
  ]);

  const setThemeAccent = useCallback(async (targetThemeId, color) => {
    const resolvedTheme = getThemeById(targetThemeId);
    const sanitizedColor = sanitizeHexColor(color);

    if (!sanitizedColor) {
      return;
    }

    const nextAccentOverrides = sanitizeThemeAccentOverrides({
      ...themeAccentOverrides,
      [resolvedTheme.id]: sanitizedColor,
    });

    syncThemeState(themeId, nextAccentOverrides, themeBackgroundOverrides, themeTextOverrides);

    try {
      await persistTheme(
        themeId,
        nextAccentOverrides,
        themeBackgroundOverrides,
        themeTextOverrides,
        currentUser
      );
    } catch (error) {
      console.error('Error saving theme accent:', error);
    }
  }, [
    currentUser,
    persistTheme,
    syncThemeState,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeId,
    themeTextOverrides,
  ]);

  const resetThemeAccent = useCallback(async (targetThemeId) => {
    const resolvedTheme = getThemeById(targetThemeId);
    const nextAccentOverrides = { ...themeAccentOverrides };

    delete nextAccentOverrides[resolvedTheme.id];

    const sanitizedOverrides = sanitizeThemeAccentOverrides(nextAccentOverrides);
    syncThemeState(themeId, sanitizedOverrides, themeBackgroundOverrides, themeTextOverrides);

    try {
      await persistTheme(
        themeId,
        sanitizedOverrides,
        themeBackgroundOverrides,
        themeTextOverrides,
        currentUser
      );
    } catch (error) {
      console.error('Error resetting theme accent:', error);
    }
  }, [
    currentUser,
    persistTheme,
    syncThemeState,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeId,
    themeTextOverrides,
  ]);

  const setThemeBackground = useCallback(async (targetThemeId, color) => {
    const resolvedTheme = getThemeById(targetThemeId);
    const sanitizedColor = sanitizeHexColor(color);

    if (!sanitizedColor) {
      return;
    }

    const nextBackgroundOverrides = sanitizeThemeBackgroundOverrides({
      ...themeBackgroundOverrides,
      [resolvedTheme.id]: sanitizedColor,
    });

    syncThemeState(themeId, themeAccentOverrides, nextBackgroundOverrides, themeTextOverrides);

    try {
      await persistTheme(
        themeId,
        themeAccentOverrides,
        nextBackgroundOverrides,
        themeTextOverrides,
        currentUser
      );
    } catch (error) {
      console.error('Error saving theme background:', error);
    }
  }, [
    currentUser,
    persistTheme,
    syncThemeState,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeId,
    themeTextOverrides,
  ]);

  const resetThemeBackground = useCallback(async (targetThemeId) => {
    const resolvedTheme = getThemeById(targetThemeId);
    const nextBackgroundOverrides = { ...themeBackgroundOverrides };

    delete nextBackgroundOverrides[resolvedTheme.id];

    const sanitizedOverrides = sanitizeThemeBackgroundOverrides(nextBackgroundOverrides);
    syncThemeState(themeId, themeAccentOverrides, sanitizedOverrides, themeTextOverrides);

    try {
      await persistTheme(
        themeId,
        themeAccentOverrides,
        sanitizedOverrides,
        themeTextOverrides,
        currentUser
      );
    } catch (error) {
      console.error('Error resetting theme background:', error);
    }
  }, [
    currentUser,
    persistTheme,
    syncThemeState,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeId,
    themeTextOverrides,
  ]);

  const setThemeText = useCallback(async (targetThemeId, color) => {
    const resolvedTheme = getThemeById(targetThemeId);
    const sanitizedColor = sanitizeHexColor(color);

    if (!sanitizedColor) {
      return;
    }

    const nextTextOverrides = sanitizeThemeTextOverrides({
      ...themeTextOverrides,
      [resolvedTheme.id]: sanitizedColor,
    });

    syncThemeState(themeId, themeAccentOverrides, themeBackgroundOverrides, nextTextOverrides);

    try {
      await persistTheme(
        themeId,
        themeAccentOverrides,
        themeBackgroundOverrides,
        nextTextOverrides,
        currentUser
      );
    } catch (error) {
      console.error('Error saving theme text:', error);
    }
  }, [
    currentUser,
    persistTheme,
    syncThemeState,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeId,
    themeTextOverrides,
  ]);

  const resetThemeText = useCallback(async (targetThemeId) => {
    const resolvedTheme = getThemeById(targetThemeId);
    const nextTextOverrides = { ...themeTextOverrides };

    delete nextTextOverrides[resolvedTheme.id];

    const sanitizedOverrides = sanitizeThemeTextOverrides(nextTextOverrides);
    syncThemeState(themeId, themeAccentOverrides, themeBackgroundOverrides, sanitizedOverrides);

    try {
      await persistTheme(
        themeId,
        themeAccentOverrides,
        themeBackgroundOverrides,
        sanitizedOverrides,
        currentUser
      );
    } catch (error) {
      console.error('Error resetting theme text:', error);
    }
  }, [
    currentUser,
    persistTheme,
    syncThemeState,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeId,
    themeTextOverrides,
  ]);

  const setCalendarCardAppearance = useCallback(async (nextAppearance) => {
    const sanitizedAppearance = sanitizeCalendarCardAppearance(nextAppearance);
    calendarCardAppearanceRef.current = sanitizedAppearance;
    setCalendarCardAppearanceState(sanitizedAppearance);
    writeCalendarCardAppearanceToLocalStorage(sanitizedAppearance);
    applyThemeToDocument(
      themeId,
      themeAccentOverrides,
      themeBackgroundOverrides,
      themeTextOverrides,
      sanitizedAppearance
    );

    if (!currentUser) {
      setCalendarCardStatus('preview');
      return;
    }

    setCalendarCardStatus('saving');
    try {
      await setDoc(
        doc(db, 'users', currentUser.uid, 'preferences', 'settings'),
        {
          calendarCardAppearance: sanitizedAppearance,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      setCalendarCardStatus('saved');
    } catch (error) {
      console.error('Error saving calendar card appearance:', error);
      setCalendarCardStatus('error');
    }
  }, [
    currentUser,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeId,
    themeTextOverrides,
  ]);

  const resetCalendarCardAppearance = useCallback(async () => {
    await setCalendarCardAppearance({ ...DEFAULT_CALENDAR_CARD_APPEARANCE });
  }, [setCalendarCardAppearance]);

  const themeMeta = useMemo(
    () => getResolvedTheme(
      themeId,
      themeAccentOverrides,
      themeBackgroundOverrides,
      themeTextOverrides,
      calendarCardAppearance
    ),
    [
      calendarCardAppearance,
      themeAccentOverrides,
      themeBackgroundOverrides,
      themeId,
      themeTextOverrides,
    ]
  );
  const theme = themeMeta.family;

  const toggleTheme = useCallback(async () => {
    const nextThemeId = getBaseThemeIdForFamily(theme === 'light' ? 'dark' : 'light');
    await setTheme(nextThemeId);
  }, [setTheme, theme]);

  const resolvedAccent = themeMeta.tokens.accent;
  const resolvedBackground = themeMeta.tokens.bg;
  const resolvedText = themeMeta.tokens.text;

  const value = useMemo(() => ({
    theme,
    themeId,
    themeFamily: theme,
    themeMeta,
    themes: THEMES,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeTextOverrides,
    calendarCardAppearance,
    calendarCardStatus,
    resolvedAccent,
    resolvedBackground,
    resolvedText,
    setTheme,
    setThemeAccent,
    resetThemeAccent,
    setThemeBackground,
    resetThemeBackground,
    setThemeText,
    resetThemeText,
    setCalendarCardAppearance,
    resetCalendarCardAppearance,
    toggleTheme,
    loading,
  }), [
    calendarCardAppearance,
    calendarCardStatus,
    loading,
    resolvedAccent,
    resolvedBackground,
    resolvedText,
    resetThemeBackground,
    resetThemeAccent,
    resetThemeText,
    resetCalendarCardAppearance,
    setTheme,
    setThemeAccent,
    setThemeBackground,
    setThemeText,
    setCalendarCardAppearance,
    theme,
    themeAccentOverrides,
    themeBackgroundOverrides,
    themeTextOverrides,
    themeId,
    themeMeta,
    toggleTheme,
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
